"use client";

import { useLang, type Lang } from "@/lib/i18n";

export interface LandingEmployeeContent {
  name: string;
  role: string;
  goal: string;
  goals: string[];
  tasks: string[];
  skills: string[];
  allSkills: string[];
  toolLabels: string[];
  allToolLabels: string[];
  statLabels: string[];
  schedule: string;
}

export interface LandingDict {
  nav: { how: string; employees: string; security: string; contact: string; demo: string };
  hero: {
    badge: string;
    titlePre: string;
    titleAccent: string;
    sub: string;
    ctaPrimary: string;
    ctaSecondary: string;
    metricWorking: string;
    metricSkills: string;
    metricConnections: string;
    metricPricingValue: string;
    metricPricingSuffix: string;
  };
  voiceWidget: {
    name: string;
    role: string;
    readyStatus: string;
    startCta: string;
    formTitle: string;
    nameLabel: string;
    namePlaceholder: string;
    phoneLabel: string;
    emailLabel: string;
    contactHelper: string;
    companyLabel: string;
    companyPlaceholder: string;
    consentLabel: string;
    submitCta: string;
    cancelCta: string;
    errorName: string;
    errorContact: string;
    errorConsent: string;
    requestingMic: string;
    micDeniedError: string;
    connecting: string;
    connectError: string;
    activeListening: string;
    activeSpeaking: string;
    endCta: string;
    endedMessage: string;
    restartCta: string;
    genericError: string;
    tryAgain: string;
  };
  employees: [LandingEmployeeContent, LandingEmployeeContent, LandingEmployeeContent];
  card: {
    performanceAria: (percent: number) => string;
    workingNow: string;
    goalsHeading: string;
    allSkillsHeading: string;
    toolsHeading: string;
    performanceHeading: string;
  };
  brainMap: {
    heading: string;
    sub: string;
    centerName: string;
    nodeLabels: [string, string, string, string, string, string, string, string];
  };
  office: {
    heading: string;
    sub: string;
    statusPill: string;
    open: string;
  };
  howItWorks: {
    heading: string;
    sub: string;
    items: [
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
    ];
  };
  skillMarketplace: {
    heading: string;
    sub: string;
    categories: [string, string, string, string, string];
  };
  aiModels: {
    heading: string;
    sub: string;
    tabs: {
      all: string;
      llm: string;
      image: string;
      video: string;
      audio: string;
    };
  };
  security: {
    heading: string;
    sub: string;
    serverTitle: string;
    serverDesc: string;
    standards: [
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
      { title: string; desc: string },
    ];
    dataStatement: string;
  };
  toolsStrip: { heading: string };
  finalCta: { heading: string; sub: string; cta: string };
  footer: { tagline: string };
}

export const LANDING_DICT: Record<Lang, LandingDict> = {
  mn: {
    nav: {
      how: "Хэрхэн ажилладаг",
      employees: "Ажилтнууд",
      security: "Аюулгүй байдал",
      contact: "Холбоо барих",
      demo: "Демо үзэх",
    },
    hero: {
      badge: "Дижитал ажилтнууд онлайн",
      titlePre: "Таны бизнесийн дараагийн ажилтан —",
      titleAccent: "AI дижитал ажилтан",
      sub: "Зорилго, ур чадвар, хуваарь, хэрэгсэлтэй AI ажилтнууд танай өдөр тутмын давтагддаг ажлыг 24/7 гүйцэтгэнэ.",
      ctaPrimary: "Дижитал ажилтан турших",
      ctaSecondary: "Демо үзэх",
      metricWorking: "ажиллана",
      metricSkills: "ур чадвар",
      metricConnections: "холболт",
      metricPricingValue: "₮3",
      metricPricingSuffix: "-с эхэлнэ",
    },
    voiceWidget: {
      name: "Ариун",
      role: "Дуут AI туслах",
      readyStatus: "Ярихад бэлэн",
      startCta: "Яриа эхлүүлэх",
      formTitle: "Ярианы өмнө мэдээллээ бөглөнө үү",
      nameLabel: "Нэр",
      namePlaceholder: "Нэрээ оруулна уу",
      phoneLabel: "Утасны дугаар",
      emailLabel: "Имэйл хаяг",
      contactHelper: "Утас эсвэл имэйлийн аль нэгийг заавал бөглөнө үү",
      companyLabel: "Байгууллага / бизнесийн төрөл (заавал биш)",
      companyPlaceholder: "Жишээ нь: онлайн дэлгүүр",
      consentLabel: "Энэ дуудлага чанарын зорилгоор бичигдэж болохыг зөвшөөрч байна",
      submitCta: "Ярилцлага эхлүүлэх",
      cancelCta: "Цуцлах",
      errorName: "Нэрээ оруулна уу",
      errorContact: "Утас эсвэл имэйлийн аль нэгийг оруулна уу",
      errorConsent: "Зөвшөөрлийг баталгаажуулна уу",
      requestingMic: "Микрофоны зөвшөөрөл хүсэж байна…",
      micDeniedError: "Микрофонд хандах зөвшөөрөл олгогдоогүй байна. Browser-ийн тохиргооноос зөвшөөрч, дахин оролдоно уу.",
      connecting: "Холбогдож байна…",
      connectError: "Холбогдож чадсангүй. Дахин оролдоно уу.",
      activeListening: "Сонсож байна…",
      activeSpeaking: "Ярьж байна…",
      endCta: "Ярилцлагыг дуусгах",
      endedMessage: "Ярилцлага дууслаа. Баярлалаа!",
      restartCta: "Дахин ярих",
      genericError: "Ямар нэг алдаа гарлаа.",
      tryAgain: "Дахин оролдох",
    },
    employees: [
      {
        name: "Маркетингийн Марлаа",
        role: "Контент & сошиал медиа",
        goal: "Веб, имэйл, аудио/видео контент бэлдэх",
        goals: [
          "Өдөрт: 3-5 пост бэлтгэх",
          "Шинэ бүх хэрэглэгчид имэйл явуулах",
          "Веб сайтад зориулсан контент бэлдэх",
          "Аудио, видео контентын санаа ба скрипт бэлдэх",
          "7 хоногт: 15-20 пост нийтлэх",
          "Сард: 2 кампанит ажил гаргах",
        ],
        tasks: [
          "Сошиал медиа контент бэлтгэж байна…",
          "Шинэ хэрэглэгчдэд имэйл илгээж байна…",
          "Веб сайтын контентын ноорог бичиж байна…",
          "Аудио, видео контентын скрипт бэлдэж байна…",
          "Кампанит ажлын үр дүн шинжилж байна…",
          "Имэйл-маркетинг илгээж байна…",
        ],
        skills: ["SEO", "Копирайтинг", "Analytics"],
        allSkills: [
          "SEO",
          "Копирайтинг",
          "Analytics",
          "Сошиал медиа",
          "Имэйл маркетинг",
          "Брэндинг",
          "Дизайн",
          "Кампанит ажил",
        ],
        toolLabels: ["Google Sheets", "Facebook", "CRM"],
        allToolLabels: ["Google Sheets", "Facebook", "CRM", "Instagram", "Mailchimp"],
        statLabels: ["Өнөөдөр", "7 хоногийн дундаж", "Дууссан даалгавар"],
        schedule: "15:00 — контент төлөвлөгөө илгээх",
      },
      {
        name: "Борлуулалтын Батаа",
        role: "Лийд & хэлэлцээр",
        goal: "7 хоногт 100 лийд олох",
        goals: [
          "Өдөрт: 100-250 SMS илгээх",
          "Өдөрт: 10-20 боломжит хэрэглэгчийн утас, имэйл цуглуулах",
          "7 хоногт: 100 шинэ лийд олох",
          "Сард: 20 гэрээ хаах",
        ],
        tasks: [
          "Интернетээс хэрэглэгчийн мэдээлэл цуглуулж байна…",
          "SMS илгээж байна…",
          "Үнийн санал бэлтгэж байна…",
        ],
        skills: ["CRM", "Хэлэлцээр", "Lead gen"],
        allSkills: [
          "CRM",
          "Хэлэлцээр",
          "Lead gen",
          "SMS илгээх",
          "Санал бэлтгэх",
          "Гэрээ хийх",
          "Upsell",
          "Хэрэглэгчийн харилцаа",
        ],
        toolLabels: ["CRM", "SMS", "Имэйл"],
        allToolLabels: ["CRM", "SMS", "Имэйл", "Чат", "Веб хайлт"],
        statLabels: ["Өнөөдөр", "7 хоногийн дундаж", "Хаасан гэрээ"],
        schedule: "17:00 — өдрийн тайлан бэлтгэх",
      },
      {
        name: "Санхүүгийн Сараа",
        role: "Тайлан & төсөв",
        goal: "Банкнаас орлого татаж, төлбөр шивэх",
        goals: [
          "Өдөрт: банкнаас орлогын мэдээлэл татах",
          "Өдөрт: санхүүгийн програмд төлбөр шивэх",
          "7 хоногт: 2 audit хийх",
          "Сард: cash flow тайлан гаргах",
        ],
        tasks: [
          "Банкнаас өдрийн орлогын мэдээлэл татаж байна…",
          "Санхүүгийн програмд төлбөр шивж байна…",
          "Сарын тайлан бэлтгэж байна…",
          "Нэхэмжлэх шалгаж байна…",
          "Төсөв тооцоолж байна…",
        ],
        skills: ["Excel", "Тайлан", "Audit"],
        allSkills: [
          "Excel",
          "Тайлан",
          "Audit",
          "Cash flow",
          "Төсөвлөлт",
          "Нэхэмжлэх",
          "Татварын тооцоо",
          "Санхүүгийн шинжилгээ",
        ],
        toolLabels: ["Excel/Sheets", "Тайлан", "Cash flow"],
        allToolLabels: ["Excel/Sheets", "Тайлан", "Cash flow", "Тооцоолол", "Нэхэмжлэх"],
        statLabels: ["Өнөөдөр", "7 хоногийн дундаж", "Шалгасан нэхэмжлэх"],
        schedule: "16:00 — cash flow тайлан илгээх",
      },
    ],
    card: {
      performanceAria: (p) => `Гүйцэтгэл ${p} хувь`,
      workingNow: "Ажиллаж байна",
      goalsHeading: "Зорилго",
      allSkillsHeading: "Бүх ур чадвар",
      toolsHeading: "Холбогдсон хэрэгсэл",
      performanceHeading: "Гүйцэтгэлийн үзүүлэлт",
    },
    brainMap: {
      heading: "Нэг ажилтны цаана юу ажилладаг вэ?",
      sub: "Зорилго, ур чадвар, хуваарь, хэрэгсэл, гүйцэтгэл — бүгд нэг дижитал ажилтны дотор.",
      centerName: "Марлаа",
      nodeLabels: ["Зорилго", "Ур чадвар", "Хуваарь", "CRM", "Sheets", "Facebook", "Тайлан", "Гүйцэтгэл"],
    },
    office: {
      heading: "Амьд ажиллаж буй дижитал оффис",
      sub: "Агентууд бодит цагт ажлын байран дээрээ хөдөлж, харилцаж, даалгавраа гүйцэтгэж байгааг ажиглаарай.",
      statusPill: "agenticforce.tech/demo · шууд",
      open: "Нээх ↗",
    },
    howItWorks: {
      heading: "Дижитал ажилтан бүр хэрхэн ажилладаг вэ?",
      sub: "Хүний адил зорилго, хуваарь, хэрэгсэлтэй — гагцхүү тасралтгүй ажилладаг.",
      items: [
        { title: "Зорилго", desc: "Сар бүр тодорхой зорилго, гүйцэтгэлийн үзүүлэлттэй ажилладаг." },
        { title: "Ур чадвар", desc: "300+ ур чадвараас тухайн үүрэгт тохирсныг сонгож өгдөг." },
        { title: "Хуваарь", desc: "24/7 хуваарийн дагуу тасралтгүй ажиллана." },
        { title: "Хэрэгсэл", desc: "Slack, Notion, Gmail зэрэг хэрэгслүүдтэй холбогдож ажилладаг." },
        { title: "Гүйцэтгэл", desc: "Гүйцэтгэлийг тоон үзүүлэлтээр байнга хэмждэг." },
      ],
    },
    skillMarketplace: {
      heading: "Ур чадварын сан",
      sub: "Манай платформ дээр бодитоор ажилладаг 300 гаруй ур чадвараас жишээ.",
      categories: ["Маркетинг", "Бизнес & Борлуулалт", "Хөгжүүлэлт", "AI & Судалгаа", "Систем & DevOps"],
    },
    aiModels: {
      heading: "Манайд ашиглагддаг моделиуд",
      sub: "Unified API-гаар LLM, зураг, видео, аудио моделиудыг нэг дор ангилж ашиглана.",
      tabs: {
        all: "Бүгд",
        llm: "LLM",
        image: "Зураг",
        video: "Видео",
        audio: "Аудио",
      },
    },
    security: {
      heading: "Аюулгүй байдал",
      sub: "Таны бизнесийн мэдээлэл болон харилцагчийн өгөгдлийг хамгаалахыг бид эн тэргүүнд тавьдаг.",
      serverTitle: "Сервер бүрэн хувийн, зөвхөн эзэмшигчийн мэдэлд",
      serverDesc:
        "agenticforce нь нийтийн (public) cloud дээр биш, зөвхөн бидний хяналтад байдаг хувийн серверт ажилладаг. Танай өгөгдөл гуравдагч талын дэд бүтэц дунд тархахгүй, ганцхан эзэмшигчийн хяналттай орчинд хадгалагдана.",
      standards: [
        {
          title: "Шифрлэгдсэн холболт (HTTPS/TLS)",
          desc: "Хэрэглэгч болон серверийн хоорондох бүх өгөгдлийн урсгал шифрлэгдсэн холболтоор дамждаг.",
        },
        {
          title: "Content Security Policy (CSP)",
          desc: "Хортой script, XSS халдлагаас хамгаалах хатуу CSP, X-Frame-Options, Referrer-Policy толгойнууд тохируулагдсан.",
        },
        {
          title: "Нууц мэдээллийн удирдлага",
          desc: "API түлхүүр, нэвтрэх мэдээллийг кодонд шууд бичдэггүй — зөвхөн орчны хувьсагч (environment variable) байдлаар хадгалдаг.",
        },
        {
          title: "Хандалтын хязгаарлалт",
          desc: "IP хаяг тус бүрээр хандалтыг хязгаарлаж, зохисгүй ашиглалт, автомат халдлагаас сэргийлдэг.",
        },
        {
          title: "Дата хадгалалт, нөөцлөлт",
          desc: "Мэдээллийн сан хандалт зөвхөн зөвшөөрөгдсөн серверийн процессоор хязгаарлагддаг бөгөөд тогтмол нөөцлөгддөг.",
        },
        {
          title: "Дотоод аюулгүй байдлын үнэлгээ",
          desc: "Код бичих, нэвтрүүлэх бүрд аюулгүй байдлын шалгалт (security review) заавал хийгддэг дадал журамтай ажилладаг.",
        },
      ],
      dataStatement:
        "Таны болон харилцагчдын мэдээллийг бид зарж, түрээслүүлж, маркетингийн зорилгоор гуравдагч этгээдэд шилжүүлдэггүй. Цуглуулсан мэдээллийг зөвхөн танай дижитал ажилтныг ажиллуулах, үйлчилгээгээ сайжруулах зорилгоор ашигладаг.",
    },
    toolsStrip: { heading: "Холбогдох системүүд" },
    finalCta: {
      heading: "Багаа өнөөдрөөс дижитал ажилтнаар өргөжүүлье",
      sub: "Ажлын урсгалаа хэлээрэй — agenticforce баг танд тохирсон дижитал ажилтныг харуулна.",
      cta: "Дижитал ажилтан ажилд авах",
    },
    footer: { tagline: "дижитал ажилтнуудын платформ" },
  },
  en: {
    nav: {
      how: "How it works",
      employees: "Employees",
      security: "Security",
      contact: "Contact",
      demo: "Watch demo",
    },
    hero: {
      badge: "Digital employees online",
      titlePre: "Your business's next hire —",
      titleAccent: "an AI digital employee",
      sub: "AI employees with goals, skills, schedules and tools handle your repetitive daily work — 24/7.",
      ctaPrimary: "Try a digital employee",
      ctaSecondary: "Watch demo",
      metricWorking: "always on",
      metricSkills: "skills",
      metricConnections: "integrations",
      metricPricingValue: "<$0.01",
      metricPricingSuffix: "starting price",
    },
    voiceWidget: {
      name: "Ariun",
      role: "Voice AI assistant",
      readyStatus: "Ready to talk",
      startCta: "Start conversation",
      formTitle: "Please fill in your details before the call",
      nameLabel: "Name",
      namePlaceholder: "Enter your name",
      phoneLabel: "Phone number",
      emailLabel: "Email address",
      contactHelper: "Please provide at least a phone number or an email",
      companyLabel: "Company / business type (optional)",
      companyPlaceholder: "e.g. online store",
      consentLabel: "I consent that this call may be recorded for quality purposes",
      submitCta: "Start the conversation",
      cancelCta: "Cancel",
      errorName: "Please enter your name",
      errorContact: "Please provide a phone number or an email",
      errorConsent: "Please confirm your consent",
      requestingMic: "Requesting microphone permission…",
      micDeniedError: "Microphone access was denied. Please allow it in your browser settings and try again.",
      connecting: "Connecting…",
      connectError: "Couldn't connect. Please try again.",
      activeListening: "Listening…",
      activeSpeaking: "Speaking…",
      endCta: "End conversation",
      endedMessage: "The conversation has ended. Thank you!",
      restartCta: "Talk again",
      genericError: "Something went wrong.",
      tryAgain: "Try again",
    },
    employees: [
      {
        name: "Marlaa",
        role: "Content & Social Media",
        goal: "Prepare web, email, audio and video content",
        goals: [
          "Daily: prepare 3-5 posts",
          "Email every new user",
          "Prepare content for the website",
          "Prepare audio and video content ideas and scripts",
          "Weekly: publish 15-20 posts",
          "Monthly: launch 2 campaigns",
        ],
        tasks: [
          "Preparing social media content…",
          "Sending email to new users…",
          "Drafting website content…",
          "Preparing audio and video scripts…",
          "Analyzing campaign results…",
          "Sending email marketing…",
        ],
        skills: ["SEO", "Copywriting", "Analytics"],
        allSkills: [
          "SEO",
          "Copywriting",
          "Analytics",
          "Social media",
          "Email marketing",
          "Branding",
          "Design",
          "Campaigns",
        ],
        toolLabels: ["Google Sheets", "Facebook", "CRM"],
        allToolLabels: ["Google Sheets", "Facebook", "CRM", "Instagram", "Mailchimp"],
        statLabels: ["Today", "7-day average", "Tasks completed"],
        schedule: "15:00 — send content plan",
      },
      {
        name: "Bataa",
        role: "Leads & Negotiation",
        goal: "Find 100 leads per week",
        goals: [
          "Daily: send 100-250 SMS",
          "Daily: collect 10-20 potential customer phones/emails",
          "Weekly: find 100 new leads",
          "Monthly: close 20 deals",
        ],
        tasks: [
          "Collecting customer info from the internet…",
          "Sending SMS…",
          "Preparing a price quote…",
        ],
        skills: ["CRM", "Negotiation", "Lead gen"],
        allSkills: [
          "CRM",
          "Negotiation",
          "Lead gen",
          "Sending SMS",
          "Preparing quotes",
          "Closing deals",
          "Upsell",
          "Customer relations",
        ],
        toolLabels: ["CRM", "SMS", "Email"],
        allToolLabels: ["CRM", "SMS", "Email", "Chat", "Web search"],
        statLabels: ["Today", "7-day average", "Deals closed"],
        schedule: "17:00 — prepare daily report",
      },
      {
        name: "Saraa",
        role: "Reporting & Budget",
        goal: "Pull bank revenue and enter payments",
        goals: [
          "Daily: pull revenue data from the bank",
          "Daily: enter payments in the finance system",
          "Weekly: run 2 audits",
          "Monthly: produce cash flow report",
        ],
        tasks: [
          "Pulling daily revenue from the bank…",
          "Entering payments in the finance system…",
          "Preparing the monthly report…",
          "Checking invoices…",
          "Calculating the budget…",
        ],
        skills: ["Excel", "Reporting", "Audit"],
        allSkills: [
          "Excel",
          "Reporting",
          "Audit",
          "Cash flow",
          "Budgeting",
          "Invoicing",
          "Tax calculation",
          "Financial analysis",
        ],
        toolLabels: ["Excel/Sheets", "Reports", "Cash flow"],
        allToolLabels: ["Excel/Sheets", "Reports", "Cash flow", "Calculations", "Invoicing"],
        statLabels: ["Today", "7-day average", "Invoices checked"],
        schedule: "16:00 — send cash flow report",
      },
    ],
    card: {
      performanceAria: (p) => `Performance ${p}%`,
      workingNow: "Working now",
      goalsHeading: "Goals",
      allSkillsHeading: "All skills",
      toolsHeading: "Connected tools",
      performanceHeading: "Performance",
    },
    brainMap: {
      heading: "What's behind a single employee?",
      sub: "Goals, skills, schedule, tools, performance — all inside one digital employee.",
      centerName: "Marlaa",
      nodeLabels: ["Goals", "Skills", "Schedule", "CRM", "Sheets", "Facebook", "Reports", "Performance"],
    },
    office: {
      heading: "A living digital office at work",
      sub: "Watch agents move around their workspace, communicate and complete tasks in real time.",
      statusPill: "agenticforce.tech/demo · live",
      open: "Open ↗",
    },
    howItWorks: {
      heading: "How does each digital employee work?",
      sub: "Just like a person — with goals, a schedule and tools — except it never stops.",
      items: [
        { title: "Goals", desc: "Works toward a clear monthly goal with measurable performance." },
        { title: "Skills", desc: "Equipped with the right skills for the role, chosen from 300+ options." },
        { title: "Schedule", desc: "Works continuously on a 24/7 schedule." },
        { title: "Tools", desc: "Connects and works with tools like Slack, Notion and Gmail." },
        { title: "Performance", desc: "Performance is constantly measured with quantifiable metrics." },
      ],
    },
    skillMarketplace: {
      heading: "Skill marketplace",
      sub: "A look at the 300+ real skills running on our platform.",
      categories: ["Marketing", "Business & Sales", "Development", "AI & Research", "Systems & DevOps"],
    },
    aiModels: {
      heading: "Models We Use",
      sub: "We route LLM, image, video and audio models through a unified API layer.",
      tabs: {
        all: "All",
        llm: "LLM",
        image: "Image",
        video: "Video",
        audio: "Audio",
      },
    },
    security: {
      heading: "Security",
      sub: "Protecting your business data and your customers' data is our top priority.",
      serverTitle: "A fully private server, controlled only by the owner",
      serverDesc:
        "agenticforce runs on a private server under our own control — not on shared public cloud infrastructure. Your data never sits spread across third-party infrastructure; it stays in an environment controlled by a single owner.",
      standards: [
        {
          title: "Encrypted connections (HTTPS/TLS)",
          desc: "All traffic between your browser and our server travels over encrypted connections.",
        },
        {
          title: "Content Security Policy (CSP)",
          desc: "Strict CSP, X-Frame-Options and Referrer-Policy headers are configured to block malicious scripts and XSS attacks.",
        },
        {
          title: "Secrets management",
          desc: "API keys and credentials are never hardcoded — they're kept only as environment variables.",
        },
        {
          title: "Access rate limiting",
          desc: "Requests are limited per IP address to prevent abuse and automated attacks.",
        },
        {
          title: "Data storage & backups",
          desc: "Database access is restricted to authorized server processes only, with regular backups.",
        },
        {
          title: "Internal security reviews",
          desc: "Every code change and deployment goes through a security review as standard practice.",
        },
      ],
      dataStatement:
        "We don't sell, rent, or transfer your data or your customers' data to third parties for marketing purposes. Collected data is used only to run your digital employee and improve our service.",
    },
    toolsStrip: { heading: "Connected systems" },
    finalCta: {
      heading: "Grow your team with a digital employee today",
      sub: "Tell us about your workflow — the agenticforce team will show you the right digital employee for you.",
      cta: "Hire a digital employee",
    },
    footer: { tagline: "the digital employees platform" },
  },
  zh: {
    nav: {
      how: "工作原理",
      employees: "数字员工",
      security: "安全保障",
      contact: "联系我们",
      demo: "观看演示",
    },
    hero: {
      badge: "数字员工在线",
      titlePre: "您企业的下一位员工 ——",
      titleAccent: "AI 数字员工",
      sub: "拥有目标、技能、日程和工具的 AI 员工，7×24 小时完成您日常的重复性工作。",
      ctaPrimary: "试用数字员工",
      ctaSecondary: "观看演示",
      metricWorking: "全天候运行",
      metricSkills: "项技能",
      metricConnections: "个集成",
      metricPricingValue: "<¥0.01",
      metricPricingSuffix: "起",
    },
    voiceWidget: {
      name: "艾茹",
      role: "语音 AI 助手",
      readyStatus: "随时可以聊",
      startCta: "开始对话",
      formTitle: "通话前请填写您的信息",
      nameLabel: "姓名",
      namePlaceholder: "请输入您的姓名",
      phoneLabel: "电话号码",
      emailLabel: "电子邮箱",
      contactHelper: "请至少填写电话号码或电子邮箱其中之一",
      companyLabel: "公司/业务类型（选填）",
      companyPlaceholder: "例如：网店",
      consentLabel: "我同意本次通话可能会被录音用于质量管理目的",
      submitCta: "开始对话",
      cancelCta: "取消",
      errorName: "请输入您的姓名",
      errorContact: "请填写电话号码或电子邮箱",
      errorConsent: "请确认同意条款",
      requestingMic: "正在请求麦克风权限…",
      micDeniedError: "麦克风访问被拒绝，请在浏览器设置中允许后重试。",
      connecting: "正在连接…",
      connectError: "连接失败，请重试。",
      activeListening: "正在聆听…",
      activeSpeaking: "正在讲话…",
      endCta: "结束对话",
      endedMessage: "对话已结束，谢谢您！",
      restartCta: "再次对话",
      genericError: "出现错误。",
      tryAgain: "重试",
    },
    employees: [
      {
        name: "玛尔拉",
        role: "内容与社交媒体",
        goal: "准备网站、邮件、音频和视频内容",
        goals: [
          "每日：准备3-5条帖子",
          "向所有新用户发送邮件",
          "为网站准备内容",
          "准备音频和视频内容创意及脚本",
          "每周：发布15-20条帖子",
          "每月：启动2场营销活动",
        ],
        tasks: [
          "正在准备社交媒体内容…",
          "正在向新用户发送邮件…",
          "正在撰写网站内容草稿…",
          "正在准备音频和视频脚本…",
          "正在分析营销活动效果…",
          "正在发送邮件营销…",
        ],
        skills: ["SEO", "文案写作", "数据分析"],
        allSkills: ["SEO", "文案写作", "数据分析", "社交媒体", "邮件营销", "品牌塑造", "设计", "营销活动"],
        toolLabels: ["Google Sheets", "Facebook", "CRM"],
        allToolLabels: ["Google Sheets", "Facebook", "CRM", "Instagram", "Mailchimp"],
        statLabels: ["今天", "7天平均", "已完成任务"],
        schedule: "15:00 — 发送内容计划",
      },
      {
        name: "巴特",
        role: "潜客与谈判",
        goal: "每周获取100个潜客",
        goals: [
          "每日：发送100-250条短信",
          "每日：收集10-20个潜在客户的电话/邮箱",
          "每周：获取100个新潜客",
          "每月：签订20份合同",
        ],
        tasks: ["正在从网络收集客户信息…", "正在发送短信…", "正在准备报价…"],
        skills: ["CRM", "谈判", "获客"],
        allSkills: ["CRM", "谈判", "获客", "发送短信", "准备报价", "签订合同", "追加销售", "客户关系"],
        toolLabels: ["CRM", "短信", "邮件"],
        allToolLabels: ["CRM", "短信", "邮件", "聊天", "网络搜索"],
        statLabels: ["今天", "7天平均", "已签合同"],
        schedule: "17:00 — 准备当日报表",
      },
      {
        name: "萨拉",
        role: "报表与预算",
        goal: "从银行拉取收入并录入付款",
        goals: [
          "每日：从银行拉取收入数据",
          "每日：在财务系统中录入付款",
          "每周：进行2次审计",
          "每月：生成现金流报表",
        ],
        tasks: [
          "正在从银行拉取每日收入数据…",
          "正在财务系统中录入付款…",
          "正在准备月度报表…",
          "正在核对发票…",
          "正在计算预算…",
        ],
        skills: ["Excel", "报表", "审计"],
        allSkills: ["Excel", "报表", "审计", "现金流", "预算编制", "开票", "税务计算", "财务分析"],
        toolLabels: ["Excel/Sheets", "报表", "现金流"],
        allToolLabels: ["Excel/Sheets", "报表", "现金流", "计算", "开票"],
        statLabels: ["今天", "7天平均", "已核对发票"],
        schedule: "16:00 — 发送现金流报表",
      },
    ],
    card: {
      performanceAria: (p) => `绩效 ${p}%`,
      workingNow: "正在工作",
      goalsHeading: "目标",
      allSkillsHeading: "全部技能",
      toolsHeading: "已连接工具",
      performanceHeading: "绩效指标",
    },
    brainMap: {
      heading: "一位数字员工的背后是什么？",
      sub: "目标、技能、日程、工具、绩效 —— 全部集于一位数字员工之中。",
      centerName: "玛尔拉",
      nodeLabels: ["目标", "技能", "日程", "CRM", "Sheets", "Facebook", "报表", "绩效"],
    },
    office: {
      heading: "正在运转的数字办公室",
      sub: "实时观看智能体在工作区中移动、交流并完成任务。",
      statusPill: "agenticforce.tech/demo · 直播",
      open: "打开 ↗",
    },
    howItWorks: {
      heading: "每位数字员工是如何工作的？",
      sub: "如同真人一样拥有目标、日程与工具 —— 只是从不停歇。",
      items: [
        { title: "目标", desc: "每月设定明确目标，并以可衡量的绩效指标开展工作。" },
        { title: "技能", desc: "从100多项技能中挑选出最适合该岗位的能力。" },
        { title: "日程", desc: "按照24/7日程持续不间断地工作。" },
        { title: "工具", desc: "可连接 Slack、Notion、Gmail 等工具协同工作。" },
        { title: "绩效", desc: "持续以量化指标衡量工作绩效。" },
      ],
    },
    skillMarketplace: {
      heading: "技能市场",
      sub: "来自我们平台上100多项真实技能的展示。",
      categories: ["市场营销", "商务与销售", "开发", "AI 与研究", "系统与运维"],
    },
    aiModels: {
      heading: "我们使用的模型",
      sub: "通过统一 API 层接入 LLM、图像、视频和音频模型。",
      tabs: {
        all: "全部",
        llm: "LLM",
        image: "图像",
        video: "视频",
        audio: "音频",
      },
    },
    security: {
      heading: "安全保障",
      sub: "保护您的业务数据和客户信息是我们的首要任务。",
      serverTitle: "服务器完全私有，仅由所有者掌控",
      serverDesc:
        "agenticforce 运行在我们自己掌控的私有服务器上，而非公共云基础设施。您的数据不会分散在第三方基础设施中，而是保存在唯一所有者掌控的环境里。",
      standards: [
        {
          title: "加密连接（HTTPS/TLS）",
          desc: "您的浏览器与我们服务器之间的所有数据传输均通过加密连接进行。",
        },
        {
          title: "内容安全策略（CSP）",
          desc: "配置了严格的 CSP、X-Frame-Options 和 Referrer-Policy 响应头，防止恶意脚本和 XSS 攻击。",
        },
        {
          title: "密钥管理",
          desc: "API 密钥和凭据从不硬编码在代码中，仅以环境变量的形式保存。",
        },
        {
          title: "访问频率限制",
          desc: "按 IP 地址限制访问频率，防止滥用和自动化攻击。",
        },
        {
          title: "数据存储与备份",
          desc: "数据库访问仅限于经授权的服务器进程，并定期进行备份。",
        },
        {
          title: "内部安全审查",
          desc: "每次代码变更与部署都会经过安全审查，这是我们的标准做法。",
        },
      ],
      dataStatement:
        "我们不会出售、出租或以营销目的将您或您客户的数据转让给第三方。所收集的数据仅用于运行您的数字员工和改进我们的服务。",
    },
    toolsStrip: { heading: "已连接系统" },
    finalCta: {
      heading: "从今天起，用数字员工扩充您的团队",
      sub: "告诉我们您的工作流程 —— agenticforce 团队将为您推荐合适的数字员工。",
      cta: "雇佣数字员工",
    },
    footer: { tagline: "数字员工平台" },
  },
};

/** Convenience hook: current language + the landing-page dictionary for it. */
export function useLandingLang() {
  const { lang, setLang } = useLang();
  return { lang, setLang, L: LANDING_DICT[lang] };
}
